import { ApolloProvider } from '@apollo/client/react'
import { Alert, CssBaseline } from '@mui/material'
import { StyledEngineProvider } from '@mui/material/styles'
import { addIntegration, tanstackRouterBrowserTracingIntegration } from '@sentry/react'
import { createRouter, RouterProvider } from '@tanstack/react-router'
import { Suspense, useMemo } from 'react'
import { HelmetProvider } from 'react-helmet-async'
import { DualThemeProvider, SitelineSnackbarProvider } from 'siteline-common-web'
import { apolloClient } from './client'
import { NotFound } from './common/components/404'
import { AdminAuthProvider, useAdminAuthContext } from './common/components/AdminAuthProvider'
import { Loader } from './common/components/Loader'
import { API_ENV } from './common/config/constants'
import { RouterContext } from './routes/__root'
import { routeTree } from './routeTree.gen'
import { devTheme, prodTheme } from './Theme'

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

// Create a new router instance
const router = createRouter({
  routeTree,
  context: { isAuthenticated: false },
  defaultNotFoundComponent: NotFound,
})

// Add router integration to Sentry
addIntegration(tanstackRouterBrowserTracingIntegration(router))

function InnerApp() {
  const { userId } = useAdminAuthContext()
  const context = useMemo((): RouterContext => ({ isAuthenticated: userId !== null }), [userId])

  return <RouterProvider router={router} context={context} />
}

// Wraps the app with the current Material theme and CssBaseline (applies a basic level of styles
// to all of our components)
export function App() {
  // Use a different color theme on staging and dev to distinguish from production
  const isProduction = API_ENV === 'production'
  const theme = isProduction ? prodTheme : devTheme

  return (
    <ApolloProvider client={apolloClient}>
      <HelmetProvider>
        <StyledEngineProvider injectFirst>
          <DualThemeProvider theme={theme}>
            <CssBaseline />
            <Suspense fallback={<Loader />}>
              <SitelineSnackbarProvider AlertComponent={Alert}>
                <AdminAuthProvider>
                  <InnerApp />
                </AdminAuthProvider>
              </SitelineSnackbarProvider>
            </Suspense>
          </DualThemeProvider>
        </StyledEngineProvider>
      </HelmetProvider>
    </ApolloProvider>
  )
}
